/**
 * Mapping route → module front pour le tracking V0.
 *
 * Utilisé par `Page Viewed` pour dériver la propriété `module` de l'enveloppe.
 * À maintenir au fil des nouvelles routes (les modules viennent de `src/modules/<name>/`).
 *
 * Convention : retourner le nom EXACT du dossier `src/modules/<name>/` pour permettre
 * `event LIKE 'Optim %'` ou `module = 'courtage'` côté requêtes ClickHouse.
 *
 * Doc : `top/docs/projects/tracking-product-plan-v1.md` (section V0).
 */

interface ModuleMapEntry {
    match: RegExp
    module: string
}

/**
 * Patterns testés dans l'ordre. Le 1er match gagne.
 * Plus spécifique en premier (ex. `/dashboard/optim...` avant `/dashboard`).
 */
const ROUTE_MODULE_MAP: readonly ModuleMapEntry[] = [
    // Alarmes V2 (« Surveillance »), avant les motifs génériques building et source
    { match: /^\/surveillances(\/|$)/, module: "alarmes-v2" },
    { match: /^\/source\/[^/]+\/surveillance(\/|$)/, module: "alarmes-v2" },
    { match: /^\/building\/[^/]+\/surveillance(\/|$)/, module: "alarmes-v2" },

    // Courtage (Optim TURPE + Comparateur)
    {
        match: /^\/dashboard\/optimisation-contractuelle/,
        module: "courtage",
    },
    {
        match: /^\/building\/[^/]+\/optimisation-contractuelle/,
        module: "courtage",
    },
    {
        match: /^\/building\/[^/]+\/comparateur-offres/,
        module: "courtage",
    },

    // Energy Manager
    { match: /^\/energy-manager/, module: "energy-manager" },

    // Data (visualisation conso, exports)
    {
        match: /^\/building\/[^/]+\/(data|compteur|capteur|station-meteo)/,
        module: "data",
    },

    // Modules Building shell
    {
        match: /^\/building\/[^/]+\/usage-profile/,
        module: "usage-profile",
    },
    { match: /^\/building\/[^/]+\/irve/, module: "irve" },
    { match: /^\/building\/[^/]+\/pv/, module: "pv" },

    // Building shell générique (vue d'ensemble bâtiment avec id)
    { match: /^\/building\/[^/]+/, module: "building" },

    // Liste bâtiments (top nav "Bâtiments") — fait partie de l'écran d'accueil
    { match: /^\/building(\/|$)/, module: "home" },

    // Source (vue compteur/capteur direct)
    { match: /^\/source(\/|$)/, module: "source" },

    // Home (folder + bâtiments list)
    { match: /^\/folder/, module: "home" },
    { match: /^\/$/, module: "home" },

    // Espace entreprise (admin)
    { match: /^\/user\/entreprise/, module: "espace-entreprise" },
    { match: /^\/user\/(alarm|settings)/, module: "user-settings" },
    { match: /^\/change-space/, module: "espace-entreprise" },

    // Onboarding
    { match: /^\/onboarding/, module: "onboarding" },
    { match: /^\/inscription/, module: "abonnement" },

    // Consentements (workflow signature mandats)
    { match: /^\/consentements/, module: "consent" },
    { match: /^\/demande/, module: "consent" },
]

/**
 * Dérive le nom du module front depuis un chemin de route.
 * Retourne `undefined` si aucune correspondance (ex: pages publiques /login).
 */
export function deriveModuleFromRoute(path: string): string | undefined {
    if (!path) return undefined
    for (const entry of ROUTE_MODULE_MAP) {
        if (entry.match.test(path)) return entry.module
    }
    return undefined
}
